// Backend lives in the `server` repo (Express + MongoDB) — the same API the
// mobile app uses. Override at build time with REACT_APP_API_BASE.
export const API_BASE =
  "https://goldentajrice.com/api" ||
  "https://goldentaj-server.vercel.app/api" ||
  // process.env.REACT_APP_API_BASE ||
  "http://localhost:5001/api";

const TOKEN_KEY = "gt_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY) || "";
export const setToken = (t) =>
  t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY);

async function request(
  path,
  { method = "GET", body, auth = false, params } = {}
) {
  const url = new URL(`${API_BASE}${path}`, window.location.origin);
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") url.searchParams.set(k, v);
    });
  }

  const headers = {};
  if (body) headers["Content-Type"] = "application/json";
  if (auth) {
    const t = getToken();
    if (t) headers.Authorization = `Bearer ${t}`;
  }

  const res = await fetch(url.toString().replace(window.location.origin, ""), {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok || data.success === false) {
    throw new Error(data.message || "Something went wrong. Please try again.");
  }
  return data;
}

// ---------------------------------------------------------------- auth
export const AuthApi = {
  sendOtp: (phone) =>
    request("/auth/send-otp", { method: "POST", body: { phone } }),
  verifyOtp: (phone, otp, referralCode) =>
    request("/auth/verify-otp", {
      method: "POST",
      body: { phone, otp, referralCode },
    }),
  me: () => request("/auth/me", { auth: true }),
  updateProfile: (payload) =>
    request("/users/me", { method: "PATCH", body: payload, auth: true }),
  deleteAccount: () =>
    request("/users/me", { method: "DELETE", auth: true }),
};

// ---------------------------------------------------------------- catalog
export const CatalogApi = {
  products: (params) => request("/products", { params }),
  product: (idOrSlug, params) => request(`/products/${idOrSlug}`, { params }),
  categories: () => request("/categories"),
};

// ---------------------------------------------------------------- shop
export const WishlistApi = {
  list: (params) => request("/wishlist", { auth: true, params }),
  ids: () => request("/wishlist/ids", { auth: true }),
  add: (productId) =>
    request("/wishlist", { method: "POST", body: { productId }, auth: true }),
  remove: (productId) =>
    request(`/wishlist/${productId}`, { method: "DELETE", auth: true }),
};

export const AddressApi = {
  list: () => request("/addresses", { auth: true }),
  create: (payload) =>
    request("/addresses", { method: "POST", body: payload, auth: true }),
  update: (id, payload) =>
    request(`/addresses/${id}`, { method: "PATCH", body: payload, auth: true }),
  remove: (id) => request(`/addresses/${id}`, { method: "DELETE", auth: true }),
};

export const OrderApi = {
  create: (payload) =>
    request("/orders", { method: "POST", body: payload, auth: true }),
  list: () => request("/orders", { auth: true }),
  get: (id) => request(`/orders/${id}`, { auth: true }),
  cancel: (id, reason) =>
    request(`/orders/${id}/cancel`, {
      method: "POST",
      body: { reason },
      auth: true,
    }),
};

export const CouponApi = {
  validate: (code, subtotal, items, pincode) =>
    request("/coupons/validate", {
      method: "POST",
      body: { code, subtotal, items, pincode },
      auth: true,
    }),
  // Coupons the admin has published for customers (filtered to their PIN code).
  public: (pincode) =>
    request("/coupons/public", { auth: true, params: { pincode } }),
};

export const PaymentApi = {
  razorpayOrder: (amount) =>
    request("/payments/razorpay/order", {
      method: "POST",
      body: { amount },
      auth: true,
    }),
};

// ---------------------------------------------------------------- meta
export const MetaApi = {
  config: () => request("/meta/config"),
  pincode: (pincode) => request("/meta/pincode", { params: { pincode } }),
  serviceability: (params) => request("/meta/serviceability", { params }),
};

export const LoyaltyApi = {
  me: () => request("/loyalty/me", { auth: true }),
  claim: (milestonePoints) =>
    request("/loyalty/claim", {
      method: "POST",
      body: { milestonePoints },
      auth: true,
    }),
};

export const ReviewApi = {
  forProduct: (productId) => request(`/reviews/product/${productId}`),
  can: (productId) => request(`/reviews/can/${productId}`, { auth: true }),
  save: (payload) =>
    request("/reviews", { method: "POST", body: payload, auth: true }),
  remove: (id) => request(`/reviews/${id}`, { method: "DELETE", auth: true }),
};

export const UploadApi = {
  // `data` is a base64 data URL; returns the hosted S3 url.
  file: (data, folder = "reviews") =>
    request("/uploads", { method: "POST", body: { data, folder }, auth: true }),
};

export const TicketApi = {
  list: () => request("/tickets", { auth: true }),
  get: (id) => request(`/tickets/${id}`, { auth: true }),
  create: (payload) =>
    request("/tickets", { method: "POST", body: payload, auth: true }),
  reply: (id, message) =>
    request(`/tickets/${id}/reply`, {
      method: "POST",
      body: { message },
      auth: true,
    }),
};

// Public config for the floating support chat — same source as the app's bot.
export const SupportApi = {
  bot: () => request("/support/bot"),
};

// Broadcasts the admin sends; read state is per-user.
export const NotificationApi = {
  list: () => request("/notifications", { auth: true }),
  unread: () => request("/notifications/unread", { auth: true }),
  markRead: () =>
    request("/notifications/read", { method: "POST", auth: true }),
};

// Leads captured by the chat bot land in the admin's Enquiries list.
export const EnquiryApi = {
  create: (payload) => request("/enquiries", { method: "POST", body: payload }),
};

export const SiteApi = {
  content: () => request("/site-content"),
};

// Promo YouTube videos shown on the home page, newest first.
export const VideoApi = {
  list: () => request("/videos"),
};

export async function submitEnquiry(payload) {
  return request("/enquiries", { method: "POST", body: payload });
}
